import { useState, useEffect } from 'react';

interface UseServiceWorkerUpdateReturn {
  showUpdateNotification: boolean;
  updateAvailable: boolean;
  performUpdate: () => void;
  dismissUpdate: () => void;
}

export const useServiceWorkerUpdate = (): UseServiceWorkerUpdateReturn => {
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    // Listen for service worker updates
    const handleServiceWorkerUpdate = (registration: ServiceWorkerRegistration) => {
      setWaitingWorker(registration.waiting);
      setUpdateAvailable(true);
      setShowUpdateNotification(true);
    };

    // Check if there's already a waiting service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration && registration.waiting) {
          handleServiceWorkerUpdate(registration);
        }
      });
    }

    // Store the handler globally so it can be called from service worker registration
    (window as any).handleServiceWorkerUpdate = handleServiceWorkerUpdate;

    return () => {
      // Clean up
      delete (window as any).handleServiceWorkerUpdate;
    };
  }, []);

  const performUpdate = async () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      setShowUpdateNotification(false);

      // Clear all caches before reloading
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            console.log('Clearing cache:', cacheName);
            return caches.delete(cacheName);
          })
        );
      }

      // Wait for the service worker to take control and clear caches
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('New service worker activated, performing hard reload...');
        // Force a hard reload that bypasses the cache
        window.location.reload();
      }, { once: true });
    }
  };

  const dismissUpdate = () => {
    setShowUpdateNotification(false);
    // Keep updateAvailable true so user can still manually refresh later if needed
  };

  return {
    showUpdateNotification,
    updateAvailable,
    performUpdate,
    dismissUpdate
  };
};